import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, Menu, Calendar as CalendarIcon, Info } from "lucide-react";
import ApiService, { ApiError } from "@/services/api";
import { EndpointKeys } from "@/config/api";
import MenuBar from "@/components/MenuBar";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
    FormState,
    ParentInformation as ParentInfo,
    Address,
} from "@/types/parent_information";
import { useNavigate, useLocation } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { useAddressValidation } from "@/hooks/useAddressValidation";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { isSuperUser } from "@/types/user";
import SuperUserEmployerSelector from "@/components/common/SuperUserEmployerSelector";
import DashboardContainer from "@/components/layout/DashboardContainer";

const initialFormState: FormState = {
    parent1_first_name: "",
    parent1_last_name: "",
    parent1_national_insurance_number: "",
    parent1_date_of_birth: null,
    parent1_phone_number: "",
    parent2_first_name: null,
    parent2_last_name: null,
    parent2_national_insurance_number: null,
    parent2_date_of_birth: null,
    parent2_phone_number: null,
    provides_accommodation: false,
    accommodation_value: null,
    rent_paid_by_nanny: null,
    first_pay_of_nanny: null,
    address: {
        street_number: "",
        route: "",
        locality: "",
        state: "",
        postal_code: "",
        country_code: "United Kingdom"
    },
    is_registered_employer: false,
    is_switching_provider: false,
    handle_hmrc_queries: null
};

export default function ParentInformation() {
    const { toast } = useToast();
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [formState, setFormState] = useState<FormState>(initialFormState);
    const { user } = useAuth();
    const [selectedUserId, setSelectedUserId] = useState<string>("current");
    const isSuperUserAccount = isSuperUser(user);
    const [hasSelectedEmployer, setHasSelectedEmployer] = useState(false);
    
    // Use address validation hook for country-specific validation
    const addressValidation = useAddressValidation();

    // Show first pay date field only for new employers who aren't switching providers
    const shouldShowFirstPayDate = !formState.is_registered_employer && !formState.is_switching_provider;

    useEffect(() => {
        if (
            !isSuperUserAccount ||
            (isSuperUserAccount && selectedUserId !== "current")
        ) {
            setHasSelectedEmployer(true);
            fetchParentInfo();
        }
    }, [selectedUserId, isSuperUserAccount]);

    // Reset first_pay_of_nanny when it shouldn't be shown
    useEffect(() => {
        if (!shouldShowFirstPayDate && formState.first_pay_of_nanny !== null) {
            setFormState(prev => ({
                ...prev,
                first_pay_of_nanny: null
            }));

            // Clear any related errors
            setErrors(prev => ({
                ...prev,
                first_pay_of_nanny: ""
            }));
        }
    }, [formState.is_registered_employer, formState.is_switching_provider]);

    // Check if the user came from the Checklist
    const isFromChecklist =
        new URLSearchParams(location.search).get("from") === "checklist";

    const handleUserChange = (userId: string) => {
        setSelectedUserId(userId);
    };

    const validateField = (name: string, value: string | number | boolean | null) => {
        let error = "";
        switch (name) {
            case "parent1_first_name":
            case "parent1_last_name":
            case "parent2_first_name":
            case "parent2_last_name":
                if (name.startsWith("parent1") && !value) {
                    error = "This field is required";
                }
                if (typeof value === 'string' && !/^[A-Za-z\s-]*$/.test(value)) {
                    error = "Name can only contain letters, spaces and hyphens";
                }
                break;
            case "parent1_national_insurance_number":
            case "parent2_national_insurance_number":
                if (name === "parent1_national_insurance_number" && !value) {
                    error = "National Insurance number is required";
                }
                if (
                    value &&
                    typeof value === 'string' &&
                    !/^[A-CEGHJ-PR-TW-Z][A-CEGHJ-NPR-TW-Z][0-9]{6}[A-D]$/.test(value)
                ) {
                    error = "Invalid National Insurance number format. Format: AB123456C";
                }
                break;
            case "parent1_phone_number":
                if (!value) {
                    error = "Phone number is required";
                }
                break;
            case "parent2_phone_number":
                // Parent 2 phone is optional, but if provided, parent2 names must be provided
                if (value && (!formState.parent2_first_name || !formState.parent2_last_name)) {
                    error = "Parent 2 name is required when phone number is provided";
                }
                break;
            case "parent1_date_of_birth":
                if (!value) {
                    error = "Date of birth is required";
                }
                if (value && typeof value === 'string') {
                    const selectedDate = new Date(value);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);

                    if (selectedDate >= today) {
                        error = "Date of birth must be in the past";
                    }
                }
                break;
            case "parent2_date_of_birth":
                // Parent 2 DOB is optional, but if provided, parent2 names must be provided
                if (value && (!formState.parent2_first_name || !formState.parent2_last_name)) {
                    error = "Parent 2 name is required when date of birth is provided";
                }
                if (value && typeof value === 'string') {
                    const selectedDate = new Date(value);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);

                    if (selectedDate >= today) {
                        error = "Date of birth must be in the past";
                    }
                }
                break;
            case "address.street_number":
                if (typeof value === 'string' && value && !/^[0-9A-Za-z]+$/.test(value)) {
                    error = "Invalid street number. You can only provide numbers or letters.";
                }
                break;
            case "address.route":
            case "address.locality":
                if (typeof value === 'string' && !value.trim()) {
                    error = "This field is required";
                }
                break;
            case "address.postal_code":
                if (!value) {
                    error = `${addressValidation.rules.postalCodeLabel} is required`;
                } else if (typeof value === 'string') {
                    const postalResult = addressValidation.validatePostalCode(value);
                    if (!postalResult.isValid) {
                        error = postalResult.error;
                    }
                }
                break;
            case "accommodation_value":
                if (formState.provides_accommodation && (!value || (typeof value === 'number' && value <= 0))) {
                    error = "Please enter a valid accommodation value greater than 0";
                }
                break;
            case "rent_paid_by_nanny":
                if (value !== null && typeof value === 'number') {
                    if (value < 0) {
                        error = "Rent paid cannot be negative";
                    } else if (formState.accommodation_value !== null && value > formState.accommodation_value) {
                        error = "Rent paid cannot exceed accommodation value";
                    }
                }
                break;
            case "first_pay_of_nanny":
                if (shouldShowFirstPayDate && !value) {
                    error = "First pay date is required for new employers";
                }
                if (value && typeof value === 'string') {
                    const selectedDate = new Date(value);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);

                    if (selectedDate < today) {
                        error = "First pay date cannot be in the past";
                    }
                }
                break;
        }
        setErrors((prev) => ({ ...prev, [name]: error }));
        return !error;
    };

    const validateForm = () => {
        let isValid = true;

        // Validate text fields
        Object.entries(formState).forEach(([key, value]) => {
            if (typeof value === "string" || (key.includes("date_of_birth") && value !== null)) {
                if (!validateField(key, value)) {
                    isValid = false;
                }
            }
        });

        // Validate required date fields
        if (!validateField("parent1_date_of_birth", formState.parent1_date_of_birth)) {
            isValid = false;
        }

        // Validate optional parent2 fields
        if (formState.parent2_date_of_birth && !validateField("parent2_date_of_birth", formState.parent2_date_of_birth)) {
            isValid = false;
        }
        if (formState.parent2_phone_number && !validateField("parent2_phone_number", formState.parent2_phone_number)) {
            isValid = false;
        }

        // Validate address fields
        if (formState.address) {
            const addressFields = ["route", "locality", "state", "postal_code"];
            addressFields.forEach(field => {
                if (!validateField(`address.${field}`, formState.address[field as keyof Address] || "")) {
                    isValid = false;
                }
            });
            
            // Validate street_number separately since it's optional
            if (formState.address.street_number) {
                if (!validateField(`address.street_number`, formState.address.street_number)) {
                    isValid = false;
                }
            }
        } else {
            isValid = false;
            setErrors(prev => ({
                ...prev,
                "address.route": "Street name is required",
                "address.locality": "Town/City is required",
                "address.state": "County is required",
                "address.postal_code": "Postcode is required"
            }));
        }

        // Validate accommodation fields
        if (formState.provides_accommodation) {
            if (!validateField("accommodation_value", formState.accommodation_value)) {
                isValid = false;
            }
            if (!validateField("rent_paid_by_nanny", formState.rent_paid_by_nanny)) {
                isValid = false;
            }
        }

        // Validate first pay date if needed
        if (shouldShowFirstPayDate) {
            if (!validateField("first_pay_of_nanny", formState.first_pay_of_nanny)) {
                isValid = false;
            }
        }

        return isValid;
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value, type } = e.target;
        let newValue: string | number | boolean | null = value;

        if (type === "checkbox") {
            newValue = (e.target as HTMLInputElement).checked;
        } else if (type === "number") {
            newValue = value === "" ? null : parseFloat(value);
        } else if (type === "date") {
            // For date inputs, keep the value as is (YYYY-MM-DD format)
            // or set to null if empty
            newValue = value === "" ? null : value;
        } else if (name.startsWith("parent2_") && value === "" && name !== "parent2_phone_number") {
            newValue = null;
        }

        setFormState((prev) => ({ ...prev, [name]: newValue }));
        validateField(name, newValue);
    };

    const handleDateChange = (fieldName: string) => (date: Date | undefined) => {
        if (!date) return;

        // Fix timezone issue by using the local date parts instead of ISO conversion
        // This ensures the selected date is preserved exactly as displayed
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
        const day = String(date.getDate()).padStart(2, '0');

        // Format as YYYY-MM-DD without timezone conversion
        const formattedDate = `${year}-${month}-${day}`;

        setFormState((prev) => ({
            ...prev,
            [fieldName]: formattedDate
        }));

        validateField(fieldName, formattedDate);
    };

    const handleSwitchChange = (name: string) => (checked: boolean) => {
        setFormState((prev) => ({ ...prev, [name]: checked }));

        // Reset accommodation fields when turning off accommodation
        if (name === "provides_accommodation" && !checked) {
            setFormState(prev => ({
                ...prev,
                accommodation_value: null,
                rent_paid_by_nanny: null
            }));

            // Clear errors for these fields
            setErrors(prev => ({
                ...prev,
                accommodation_value: "",
                rent_paid_by_nanny: ""
            }));
        }
    };

    const handleAddressChange = (field: keyof Address) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setFormState((prev) => ({
            ...prev,
            address: {
                ...prev.address,
                [field]: value
            }
        }));
        validateField(`address.${field}`, value);
    };

    const fetchParentInfo = async () => {
        try {
            setIsLoading(true);
            const queryParams: Record<string, string | number> = {};
            if (selectedUserId !== "current") {
                queryParams.userId = selectedUserId;
            }

            const response = await ApiService.request<{
                parent_info: ParentInfo;
            }>(EndpointKeys.GET_PARENT_INFO, {
                queryParams,
            });

            // Transform the response to ensure all optional fields have a value
            setFormState({
                ...response.parent_info,
                parent1_date_of_birth: response.parent_info.parent1_date_of_birth || null,
                parent1_phone_number: response.parent_info.parent1_phone_number || "",
                parent2_first_name: response.parent_info.parent2_first_name || null,
                parent2_last_name: response.parent_info.parent2_last_name || null,
                parent2_national_insurance_number:
                    response.parent_info.parent2_national_insurance_number || null,
                parent2_date_of_birth: response.parent_info.parent2_date_of_birth || null,
                parent2_phone_number: response.parent_info.parent2_phone_number || null,
                // Use the address from the response or fall back to the default address
                address: response.parent_info.address ? {
                    street_number: response.parent_info.address.street_number || "",
                    route: response.parent_info.address.route || "",
                    locality: response.parent_info.address.locality || "",
                    state: response.parent_info.address.state || "",
                    postal_code: response.parent_info.address.postal_code || "",
                    country_code: response.parent_info.address.country_code || "United Kingdom"
                } : {
                    street_number: "",
                    route: "",
                    locality: "",
                    state: "",
                    postal_code: "",
                    country_code: "United Kingdom"
                }
            });
        } catch (err) {
            if (err instanceof ApiError && err.status === 404) {
                // If no parent info exists, keep the initial form state
                return;
            }
            const errorMessage =
                err instanceof Error ? err.message : "An unexpected error occurred";
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!validateForm()) {
            toast({
                title: "Error",
                description: "Please correct the errors before submitting",
                variant: "destructive",
            });
            return;
        }

        try {
            setIsLoading(true);
            setError(null);

            const queryParams: Record<string, string | number> = {};
            if (selectedUserId !== "current") {
                queryParams.userId = selectedUserId;
            }

            await ApiService.request<{ parent_info: ParentInfo }>(
                EndpointKeys.CREATE_UPDATE_PARENT_INFO,
                {
                    method: "POST",
                    body: formState,
                    queryParams,
                },
            );

            toast({
                title: "Success",
                description: "Parent information saved successfully",
            });

            // Only navigate back to dashboard if the user came from the Checklist
            if (isFromChecklist) {
                setTimeout(() => {
                    navigate("/dashboard");
                }, 1500); // 1.5 second delay before navigation
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "An error occurred");
            toast({
                title: "Error",
                description: "Failed to save parent information",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const HeaderContent = () => {
        if (!isSuperUserAccount) return null;

        return (
            <SuperUserEmployerSelector
                selectedUserId={selectedUserId}
                onUserChange={handleUserChange}
            />
        );
    };

    if (isLoading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
        );
    }

    if (isSuperUserAccount && !hasSelectedEmployer) {
        return (
            <DashboardContainer
                title="Parent Information"
                headerContent={<HeaderContent />}
            >
                <div className="flex flex-col items-center justify-center p-12 text-center">
                    <div className="mb-4 rounded-full bg-yellow-100 p-4">
                        <Info className="h-8 w-8 text-yellow-500" />
                    </div>
                    <h2 className="mb-2 text-xl font-semibold">
                        Please Select an Employer
                    </h2>
                    <p className="max-w-md text-gray-600">
                        As a superuser, you need to select an employer from the dropdown
                        above before viewing parent information.
                    </p>
                </div>
            </DashboardContainer>
        );
    }

    return (
        <DashboardContainer
            title="Parent Information"
            headerContent={<HeaderContent />}
        >
            <Alert className="mb-6">
                <Info className="h-4 w-4 text-primary" />
                <AlertTitle>Manage Parent Details</AlertTitle>
                <AlertDescription>
                    This is where you enter information about yourself as a parent.
                    While we only strictly need one parent's details to register you with HMRC and the Pensions Regulator,
                    providing information for a second parent (optional) allows them to access the account later for authentication purposes.
                    We will keep all information private and only use it for registrations and other employer-related activities.
                </AlertDescription>
            </Alert>

            {error && (
                <Alert variant="destructive" className="mb-6">
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Parent 1 Section */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Parent 1</h3>
                    <div className="grid gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="parent1_first_name">First Name</Label>
                            <Input
                                id="parent1_first_name"
                                name="parent1_first_name"
                                value={formState.parent1_first_name}
                                onChange={handleChange}
                                className={
                                    errors.parent1_first_name ? "border-red-500" : ""
                                }
                            />
                            {errors.parent1_first_name && (
                                <p className="text-red-500 text-sm">
                                    {errors.parent1_first_name}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="parent1_last_name">Last Name</Label>
                            <Input
                                id="parent1_last_name"
                                name="parent1_last_name"
                                value={formState.parent1_last_name}
                                onChange={handleChange}
                                className={
                                    errors.parent1_last_name ? "border-red-500" : ""
                                }
                            />
                            {errors.parent1_last_name && (
                                <p className="text-red-500 text-sm">
                                    {errors.parent1_last_name}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="parent1_national_insurance_number">
                                National Insurance Number
                            </Label>
                            <Input
                                id="parent1_national_insurance_number"
                                name="parent1_national_insurance_number"
                                value={formState.parent1_national_insurance_number}
                                onChange={handleChange}
                                className={
                                    errors.parent1_national_insurance_number
                                        ? "border-red-500"
                                        : ""
                                }
                            />
                            {errors.parent1_national_insurance_number && (
                                <p className="text-red-500 text-sm">
                                    {errors.parent1_national_insurance_number}
                                </p>
                            )}
                            <p className="text-sm text-gray-500">
                                Format: 2 letters, 6 numbers, 1 letter (e.g., AB123456C)
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="parent1_date_of_birth">
                                Date of Birth
                            </Label>
                            <Input
                                id="parent1_date_of_birth"
                                name="parent1_date_of_birth"
                                data-testid="parent1-date-of-birth"
                                type="date"
                                value={formState.parent1_date_of_birth || ""}
                                onChange={handleChange}
                                max={new Date().toISOString().split('T')[0]} // Max date is today
                                className={
                                    errors.parent1_date_of_birth ? "border-red-500" : ""
                                }
                            />
                            {errors.parent1_date_of_birth && (
                                <p className="text-red-500 text-sm">
                                    {errors.parent1_date_of_birth}
                                </p>
                            )}
                            <p className="text-sm text-gray-500">
                                Format: DD/MM/YYYY
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="parent1_phone_number">
                                Phone Number
                            </Label>
                            <Input
                                id="parent1_phone_number"
                                name="parent1_phone_number"
                                data-testid="parent1-phone-number"
                                value={formState.parent1_phone_number}
                                onChange={handleChange}
                                placeholder="e.g., 07123456789"
                                className={
                                    errors.parent1_phone_number ? "border-red-500" : ""
                                }
                            />
                            {errors.parent1_phone_number && (
                                <p className="text-red-500 text-sm">
                                    {errors.parent1_phone_number}
                                </p>
                            )}
                            <p className="text-sm text-gray-500">
                                UK mobile or landline number
                            </p>
                        </div>
                    </div>
                </div>

                {/* Address Section */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Address</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="address.street_number">
                                Street Number
                            </Label>
                            <Input
                                id="address.street_number"
                                name="address.street_number"
                                value={formState.address.street_number}
                                onChange={handleAddressChange("street_number")}
                                className={errors["address.street_number"] ? "border-red-500" : ""}
                            />
                            {errors["address.street_number"] && (
                                <p className="text-red-500 text-sm">
                                    {errors["address.street_number"]}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="address.route">
                                Street Name
                                <span className="text-red-500 ml-1">*</span>
                            </Label>
                            <Input
                                id="address.route"
                                name="address.route"
                                value={formState.address.route}
                                onChange={handleAddressChange("route")}
                                className={errors["address.route"] ? "border-red-500" : ""}
                            />
                            {errors["address.route"] && (
                                <p className="text-red-500 text-sm">
                                    {errors["address.route"]}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="address.locality">
                                Town/City
                                <span className="text-red-500 ml-1">*</span>
                            </Label>
                            <Input
                                id="address.locality"
                                name="address.locality"
                                value={formState.address.locality}
                                onChange={handleAddressChange("locality")}
                                className={errors["address.locality"] ? "border-red-500" : ""}
                            />
                            {errors["address.locality"] && (
                                <p className="text-red-500 text-sm">
                                    {errors["address.locality"]}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="address.state">County</Label>
                            <Input
                                id="address.state"
                                name="address.state"
                                value={formState.address.state}
                                onChange={handleAddressChange("state")}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="address.postal_code">
                                Postcode
                                <span className="text-red-500 ml-1">*</span>
                            </Label>
                            <Input
                                id="address.postal_code"
                                name="address.postal_code"
                                value={formState.address.postal_code}
                                onChange={handleAddressChange("postal_code")}
                                className={errors["address.postal_code"] ? "border-red-500" : ""}
                            />
                            {errors["address.postal_code"] && (
                                <p className="text-red-500 text-sm">
                                    {errors["address.postal_code"]}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="address.country_code">
                                Country
                                <span className="text-red-500 ml-1">*</span>
                            </Label>
                            <Input
                                id="address.country_code"
                                name="address.country_code"
                                value={formState.address.country_code}
                                readOnly={true}
                                onChange={handleAddressChange("country_code")}
                            />
                        </div>
                    </div>
                </div>

                {/* Parent 2 Section */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Parent 2 (Optional)</h3>
                    <div className="grid gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="parent2_first_name">First Name</Label>
                            <Input
                                id="parent2_first_name"
                                name="parent2_first_name"
                                value={formState.parent2_first_name || ""}
                                onChange={handleChange}
                                className={
                                    errors.parent2_first_name ? "border-red-500" : ""
                                }
                            />
                            {errors.parent2_first_name && (
                                <p className="text-red-500 text-sm">
                                    {errors.parent2_first_name}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="parent2_last_name">Last Name</Label>
                            <Input
                                id="parent2_last_name"
                                name="parent2_last_name"
                                value={formState.parent2_last_name || ""}
                                onChange={handleChange}
                                className={
                                    errors.parent2_last_name ? "border-red-500" : ""
                                }
                            />
                            {errors.parent2_last_name && (
                                <p className="text-red-500 text-sm">
                                    {errors.parent2_last_name}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="parent2_national_insurance_number">
                                National Insurance Number
                            </Label>
                            <Input
                                id="parent2_national_insurance_number"
                                name="parent2_national_insurance_number"
                                value={formState.parent2_national_insurance_number || ""}
                                onChange={handleChange}
                                className={
                                    errors.parent2_national_insurance_number
                                        ? "border-red-500"
                                        : ""
                                }
                            />
                            {errors.parent2_national_insurance_number && (
                                <p className="text-red-500 text-sm">
                                    {errors.parent2_national_insurance_number}
                                </p>
                            )}
                            <p className="text-sm text-gray-500">
                                Format: 2 letters, 6 numbers, 1 letter (e.g., AB123456C)
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="parent2_date_of_birth">
                                Date of Birth
                            </Label>
                            <Input
                                id="parent2_date_of_birth"
                                name="parent2_date_of_birth"
                                data-testid="parent2-date-of-birth"
                                type="date"
                                value={formState.parent2_date_of_birth || ""}
                                onChange={handleChange}
                                max={new Date().toISOString().split('T')[0]} // Max date is today
                                className={
                                    errors.parent2_date_of_birth ? "border-red-500" : ""
                                }
                            />
                            {errors.parent2_date_of_birth && (
                                <p className="text-red-500 text-sm">
                                    {errors.parent2_date_of_birth}
                                </p>
                            )}
                            <p className="text-sm text-gray-500">
                                Format: DD/MM/YYYY
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="parent2_phone_number">
                                Phone Number
                            </Label>
                            <Input
                                id="parent2_phone_number"
                                name="parent2_phone_number"
                                data-testid="parent2-phone-number"
                                value={formState.parent2_phone_number || ""}
                                onChange={handleChange}
                                placeholder="e.g., 07123456789"
                                className={
                                    errors.parent2_phone_number ? "border-red-500" : ""
                                }
                            />
                            {errors.parent2_phone_number && (
                                <p className="text-red-500 text-sm">
                                    {errors.parent2_phone_number}
                                </p>
                            )}
                            <p className="text-sm text-gray-500">
                                UK mobile or landline number
                            </p>
                        </div>
                    </div>
                </div>

                {/* Additional Information Section */}
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold">
                        Additional Information
                    </h3>

                    {!formState.is_registered_employer &&
                        formState.is_switching_provider && (
                            <Alert variant="destructive" className="mb-6">
                                <AlertTitle>Error</AlertTitle>
                                <AlertDescription>
                                    You must be registered as an employer if you are coming
                                    from another payroll provider.
                                </AlertDescription>
                            </Alert>
                        )}

                    <div className="grid gap-4">
                        <div className="flex flex-col md:flex-row md:items-center justify-between rounded-lg border p-4">
                            <div className="space-y-0.5 mb-2 md:mb-0">
                                <Label>Registered Employer</Label>
                                <p className="text-sm text-gray-500">
                                    Are you already registered as an employer with HMRC?
                                    (This is usually done if you've used another payroll provider or contacted HMRC directly).
                                </p>
                            </div>
                            <Switch
                                checked={formState.is_registered_employer}
                                onCheckedChange={handleSwitchChange(
                                    "is_registered_employer",
                                )}
                            />
                        </div>

                        <div className="flex flex-col md:flex-row md:items-center justify-between rounded-lg border p-4">
                            <div className="space-y-0.5 mb-2 md:mb-0">
                                <Label>Switching Provider</Label>
                                <p className="text-sm text-gray-500">
                                    Are you moving from another payroll service provider?
                                </p>
                            </div>
                            <Switch
                                checked={formState.is_switching_provider}
                                onCheckedChange={handleSwitchChange(
                                    "is_switching_provider",
                                )}
                            />
                        </div>

                        {/* Accommodation Toggle */}
                        <div className="flex flex-col md:flex-row md:items-center justify-between rounded-lg border p-4">
                            <div className="space-y-0.5 mb-2 md:mb-0">
                                <Label>Provide Accommodation</Label>
                                <p className="text-sm text-gray-500">
                                    Do you provide living accommodation to your nanny?
                                    (This is considered a taxable benefit and we need this information to calculate taxes correctly for HMRC.)
                                </p>
                            </div>
                            <Switch
                                checked={formState.provides_accommodation}
                                onCheckedChange={handleSwitchChange(
                                    "provides_accommodation",
                                )}
                            />
                        </div>
                    </div>

                    {formState.is_switching_provider && (
                        <div className="space-y-4 rounded-lg border p-4 md:p-6">
                            <div className="space-y-2">
                                <Label className="text-base font-semibold">
                                    HMRC Queries Handling
                                </Label>
                                <p className="text-sm text-gray-500">
                                    Please select how you would like your HMRC queries to be
                                    handled
                                </p>
                            </div>
                            <RadioGroup
                                value={formState.handle_hmrc_queries ? "true" : "false"}
                                onValueChange={(value) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        handle_hmrc_queries: value === "true",
                                    }))
                                }
                                className="space-y-4"
                            >
                                <div className="flex items-start space-x-3 space-y-0">
                                    <RadioGroupItem value="true" id="hmrc-handle" />
                                    <Label
                                        htmlFor="hmrc-handle"
                                        className="font-normal leading-relaxed cursor-pointer"
                                    >
                                        <span className="font-medium">
                                            We at NannyPay will handle all HMRC queries on your
                                            behalf for no extra cost
                                        </span>
                                    </Label>
                                </div>

                                <div className="flex items-start space-x-3 space-y-0">
                                    <RadioGroupItem value="false" id="hmrc-no-handle" />
                                    <Label
                                        htmlFor="hmrc-no-handle"
                                        className="font-normal leading-relaxed cursor-pointer"
                                    >
                                        <span className="font-medium">
                                            I do not want NannyPay to handle my HMRC queries and
                                            issues
                                        </span>
                                    </Label>
                                </div>
                            </RadioGroup>

                            {formState.handle_hmrc_queries && (
                                <div className="mt-6 space-y-4 bg-gray-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-lg">How to Authorise NannyPay as Your HMRC Agent</h4>

                                    <div className="text-sm space-y-4">
                                        <Alert>
                                            <Info className="h-4 w-4 text-primary" />
                                            <AlertTitle>Important Note</AlertTitle>
                                            <AlertDescription>
                                                If you currently have another agent managing your HMRC account, you'll need to remove them before adding NannyPay as your new agent.
                                            </AlertDescription>
                                        </Alert>

                                        <div className="space-y-2">
                                            <p className="font-medium">Information You'll Need:</p>
                                            <ul className="list-none space-y-1">
                                                <li className="flex items-center">
                                                    <span className="text-green-600 mr-2">✔</span> Your HMRC login details (username & password)
                                                </li>
                                                <li className="flex items-center">
                                                    <span className="text-green-600 mr-2">✔</span> NannyPay's Government Gateway ID (we'll provide this separately)
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="space-y-2">
                                            <p className="font-medium">Step-by-Step Instructions:</p>
                                            <ol className="list-decimal pl-5 space-y-3">
                                                <li>
                                                    <p className="font-medium">Log in to Your HMRC Business Account</p>
                                                    <ul className="list-none pl-4 space-y-1">
                                                        <li className="flex items-center">
                                                            <span className="text-blue-600 mr-2">➡</span>{" "}
                                                            <a
                                                                href="https://www.gov.uk/log-in-register-hmrc-online-services"
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-blue-600 hover:text-blue-800 underline"
                                                            >
                                                                Go to HMRC Online Services
                                                            </a>
                                                        </li>
                                                        <li className="flex items-center">
                                                            <span className="text-blue-600 mr-2">➡</span> Enter your Government Gateway username and password
                                                        </li>
                                                    </ul>
                                                </li>
                                                <li>
                                                    <p className="font-medium">Navigate to Agent Authorisation</p>
                                                    <ul className="list-none pl-4 space-y-1">
                                                        <li className="flex items-center">
                                                            <span className="text-blue-600 mr-2">➡</span> Look for "Manage Account" or "Tax Agent Access"
                                                        </li>
                                                        <li className="flex items-center">
                                                            <span className="text-blue-600 mr-2">➡</span> Click "Add, View or Change Tax Agents"
                                                        </li>
                                                    </ul>
                                                </li>
                                                <li>
                                                    <p className="font-medium">Select Services to Authorise</p>
                                                    <ul className="list-none pl-4 space-y-1">
                                                        <li className="flex items-center">
                                                            <span className="text-blue-600 mr-2">➡</span> Choose PAYE for employers
                                                        </li>
                                                        <li className="flex items-center">
                                                            <span className="text-blue-600 mr-2">➡</span> Click "Change or Add an Agent"
                                                        </li>
                                                    </ul>
                                                </li>
                                                <li>
                                                    <p className="font-medium">Enter NannyPay's Agent Details</p>
                                                    <ul className="list-none pl-4">
                                                        <li className="flex items-center">
                                                            <span className="text-blue-600 mr-2">➡</span> Enter our Government Gateway Agent ID when prompted
                                                        </li>
                                                    </ul>
                                                </li>
                                                <li>
                                                    <p className="font-medium">Confirm the Authorisation</p>
                                                    <ul className="list-none pl-4">
                                                        <li className="flex items-center">
                                                            <span className="text-blue-600 mr-2">➡</span> Review the details and click "Confirm & Send"
                                                        </li>
                                                    </ul>
                                                </li>
                                            </ol>
                                        </div>

                                        <div className="space-y-2">
                                            <p className="font-medium">What Happens Next?</p>
                                            <ul className="list-none space-y-1">
                                                <li className="flex items-center">
                                                    <span className="text-blue-600 mr-2">🔹</span> Once authorised, we'll be able to manage your PAYE and communicate with HMRC on your behalf
                                                </li>
                                                <li className="flex items-center">
                                                    <span className="text-blue-600 mr-2">🔹</span> You can log in anytime to view, change, or remove our access
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Accommodation Details Section - shown conditionally */}
                    {formState.provides_accommodation && (
                        <div className="space-y-4 rounded-lg border p-4 md:p-6">
                            <div className="space-y-2">
                                <Label className="text-base font-semibold">
                                    Accommodation Details
                                </Label>
                                <p className="text-sm text-gray-500">
                                    Please provide details about the accommodation you offer to your nanny
                                </p>
                            </div>

                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="accommodation_value">
                                        Accommodation Value (£ per month)*
                                    </Label>
                                    <Input
                                        id="accommodation_value"
                                        name="accommodation_value"
                                        type="number"
                                        value={formState.accommodation_value ?? ""}
                                        onChange={handleChange}
                                        className={errors.accommodation_value ? "border-red-500" : ""}
                                        placeholder="e.g. 500"
                                        min="0"
                                        step="0.01"
                                        required
                                    />
                                    {errors.accommodation_value && (
                                        <p className="text-red-500 text-sm">
                                            {errors.accommodation_value}
                                        </p>
                                    )}
                                    <p className="text-sm text-gray-500">
                                        The fair market value of the accommodation provided
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="rent_paid_by_nanny">
                                        Rent Paid by Nanny (£ per month)
                                    </Label>
                                    <Input
                                        id="rent_paid_by_nanny"
                                        name="rent_paid_by_nanny"
                                        type="number"
                                        value={formState.rent_paid_by_nanny ?? ""}
                                        onChange={handleChange}
                                        className={errors.rent_paid_by_nanny ? "border-red-500" : ""}
                                        placeholder="e.g. 250"
                                        min="0"
                                        step="0.01"
                                    />
                                    {errors.rent_paid_by_nanny && (
                                        <p className="text-red-500 text-sm">
                                            {errors.rent_paid_by_nanny}
                                        </p>
                                    )}
                                    <p className="text-sm text-gray-500">
                                        Amount the nanny pays for the accommodation (if any)
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* First Pay Date Section - shown only for new employers who aren't switching providers */}
                    {shouldShowFirstPayDate && (
                        <div className="space-y-4 rounded-lg border p-4 md:p-6">
                            <div className="space-y-2">
                                <Label className="text-base font-semibold">
                                    First Payment Details
                                </Label>
                                <p className="text-sm text-gray-500">
                                    Please tell us when you plan to pay your nanny for the first time
                                </p>
                            </div>

                            <div className="grid gap-2 mt-2">
                                <Label htmlFor="first_pay_of_nanny">
                                    First Pay Date*
                                </Label>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            id="first_pay_of_nanny"
                                            variant={"outline"}
                                            className={cn(
                                                "w-full justify-start text-left font-normal",
                                                !formState.first_pay_of_nanny && "text-muted-foreground",
                                                errors.first_pay_of_nanny && "border-red-500"
                                            )}
                                        >
                                            <CalendarIcon className="mr-2 h-4 w-4" />
                                            {formState.first_pay_of_nanny ?
                                                format(new Date(`${formState.first_pay_of_nanny}T12:00:00`), "PPP") :
                                                <span>Select a date</span>}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={formState.first_pay_of_nanny ?
                                                new Date(`${formState.first_pay_of_nanny}T12:00:00`) :
                                                undefined
                                            }
                                            onSelect={handleDateChange("first_pay_of_nanny")}
                                            disabled={(date) => {
                                                const today = new Date();
                                                today.setHours(0, 0, 0, 0);
                                                return date < today;
                                            }}
                                            classNames={{
                                                nav_button: "h-7 w-7 bg-transparent p-0 text-gray-700 hover:text-gray-900",
                                                caption_label: "text-sm font-medium text-gray-900",
                                                day_outside: "day-outside text-gray-600 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
                                                day_disabled: "text-gray-400 opacity-50 cursor-not-allowed",
                                                day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 text-gray-900 hover:bg-accent hover:text-accent-foreground"
                                            }}
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>
                                {errors.first_pay_of_nanny && (
                                    <p className="text-red-500 text-sm">
                                        {errors.first_pay_of_nanny}
                                    </p>
                                )}
                                <p className="text-sm text-gray-500">
                                    This will help us set up your HMRC registration correctly
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                <Button
                    type="submit"
                    className="w-full md:w-auto"
                    disabled={
                        isLoading ||
                        (!formState.is_registered_employer &&
                            formState.is_switching_provider &&
                            formState.handle_hmrc_queries === null)
                    }
                >
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save Information
                </Button>
            </form>
        </DashboardContainer>
    );
}
